/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DndProvider, DropTargetMonitor, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Flex } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import Tooltip from '@totem/components/common/tooltip/Tooltip';
import {
  ASSIGNEE_TYPE_LABELS,
  TemplateTask,
} from '@totem/components/ticketing/templateManagement/types';
import {
  TASK_TYPE_GROUP,
  TASK_TYPE_MILESTONE,
  TASK_TYPE_TASK,
} from '@totem/components/ticketing/ticketing';
import { formatDuration } from '@totem/components/ticketing/utilities';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import Hours from '@totem/components/ui_components/widgets/hours/hours';
import IBDIcon from '@totem/styles/Icon';
import { formatDateOnly, isNotNull } from '@totem/utilities/common';

export type DropPosition = 'above' | 'below' | 'inside';

export type HierarchicalTemplateTask = TemplateTask & {
  children?: HierarchicalTemplateTask[];
};

type Props = {
  tasks: TemplateTask[];
  onMoveTask: (
    draggedTaskNumber: number,
    targetTaskNumber: number,
    position: DropPosition,
  ) => void;
  onEditTask: (task: TemplateTask) => void;
  onDeleteTask: (task: TemplateTask) => void;
};

const buildTemplateTaskHierarchy = (
  flatTasks: TemplateTask[],
): HierarchicalTemplateTask[] => {
  if (!flatTasks || flatTasks.length === 0) {
    return [];
  }

  const taskMap = new Map<number, HierarchicalTemplateTask>();

  flatTasks.forEach((task) => {
    taskMap.set(task.number, { ...task, children: [] });
  });

  const rootTasks: HierarchicalTemplateTask[] = [];

  taskMap.forEach((task) => {
    if (task.parent === 0) {
      rootTasks.push(task);
    } else {
      const parentTask = taskMap.get(task.parent);
      if (parentTask) {
        if (!parentTask.children) {
          parentTask.children = [];
        }
        parentTask.children.push(task);
      } else {
        rootTasks.push(task);
      }
    }
  });

  const sortChildrenRecursively = (tasksToSort: HierarchicalTemplateTask[]) => {
    tasksToSort.forEach((task) => {
      if (task.children && task.children.length > 0) {
        task.children.sort((compA, compB) => compA.position - compB.position);

        sortChildrenRecursively(task.children);
      }
    });
  };

  rootTasks.sort((compA, compB) => compA.position - compB.position);

  sortChildrenRecursively(rootTasks);

  return rootTasks;
};

const getAllExpandableKeys = (taskList: HierarchicalTemplateTask[]): string[] =>
  taskList.flatMap((task) =>
    task.children?.length
      ? [task.id, ...getAllExpandableKeys(task.children)]
      : [],
  );

const TemplateTasksTable = ({
  tasks,
  onMoveTask,
  onEditTask,
  onDeleteTask,
}: Props) => {
  const DND_ITEM_TYPE = 'TEMPLATE_TASK_ROW';

  type DragItem = {
    taskNumber: number;
  };

  type DragRowProps = React.HTMLAttributes<HTMLTableRowElement> & {
    index?: number;
    record?: HierarchicalTemplateTask;
    moveTask?: (
      draggedTaskNumber: number,
      targetTaskNumber: number,
      position: DropPosition,
    ) => void;
  };

  const getDropPosition = (
    monitor: DropTargetMonitor<DragItem, void>,
    ref: React.RefObject<HTMLTableRowElement>,
    allowInside: boolean,
  ): DropPosition | null => {
    const clientOffset = monitor.getClientOffset();
    const element = ref.current;
    if (!clientOffset || !element) {
      return null;
    }

    const rect = element.getBoundingClientRect();
    const hoverY = clientOffset.y - rect.top;
    const height = rect.bottom - rect.top;

    if (hoverY < height / 3) {
      return 'above';
    }
    if (hoverY > (height * 2) / 3) {
      return 'below';
    }
    if (allowInside) {
      return 'inside';
    }
    return hoverY < height / 2 ? 'above' : 'below';
  };

  const DraggableTaskRow = ({
    index,
    record,
    moveTask,
    className,
    style,
    ...restProps
  }: DragRowProps) => {
    const ref = useRef<HTMLTableRowElement>(null);

    const [{ isDragging }, drag] = useDrag({
      type: DND_ITEM_TYPE,
      item: { taskNumber: record ? record.number : -1 } as DragItem,
      canDrag: !!record,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    const [{ isOver, dropPosition }, drop] = useDrop<
      DragItem,
      void,
      { isOver: boolean; dropPosition: DropPosition | null }
    >({
      accept: DND_ITEM_TYPE,
      canDrop: (item) => isNotNull(record) && item.taskNumber !== record.number,
      drop: (item, monitor) => {
        if (!record || !moveTask) return;
        const position = getDropPosition(
          monitor,
          ref,
          record.type === TASK_TYPE_GROUP,
        );
        if (position) {
          moveTask(item.taskNumber, record.number, position);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
        dropPosition:
          record && monitor.isOver({ shallow: true })
            ? getDropPosition(monitor, ref, record.type === TASK_TYPE_GROUP)
            : null,
      }),
    });

    drag(drop(ref));

    const dropStyles: React.CSSProperties = {};
    if (isOver && dropPosition === 'above') {
      dropStyles.boxShadow = 'inset 0 3px 0 0 #1890ff';

      dropStyles.backgroundColor = '#f0f5ff';
    } else if (isOver && dropPosition === 'below') {
      dropStyles.boxShadow = 'inset 0 -3px 0 0 #1890ff';

      dropStyles.backgroundColor = '#f0f5ff';
    } else if (isOver && dropPosition === 'inside') {
      dropStyles.boxShadow = 'inset 0 0 0 3px #1890ff';

      dropStyles.backgroundColor = '#f0f5ff';
    }

    return (
      <tr
        {...restProps}
        ref={ref}
        className={className}
        style={{
          cursor: record ? 'grab' : 'default',
          opacity: isDragging ? 0.3 : 1,
          ...dropStyles,
          ...style,
        }}
        data-row-index={index}
      />
    );
  };

  const taskHierarchy = useMemo(
    () => buildTemplateTaskHierarchy(tasks),
    [tasks],
  );

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>(() =>
    getAllExpandableKeys(taskHierarchy),
  );

  useEffect(() => {
    setExpandedRowKeys((prev) => {
      const newKeys = getAllExpandableKeys(taskHierarchy).filter(
        (key) => !prev.includes(key),
      );
      return newKeys.length > 0 ? [...prev, ...newKeys] : prev;
    });
  }, [taskHierarchy]);

  const columns: ColumnProps<TemplateTask>[] = [
    {
      title: 'ID',
      dataIndex: 'number',
      key: 'number',
      render: (_, record: TemplateTask) => (
        <span style={{ cursor: 'pointer' }} onClick={() => onEditTask(record)}>
          <IBDIcon name={record.type} style={{ marginRight: '16px' }} />
          {record.number}
        </span>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (_, record: TemplateTask) => (
        <span style={{ cursor: 'pointer' }} onClick={() => onEditTask(record)}>
          {record.name}
        </span>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Expected Duration',
      dataIndex: 'expectedDuration',
      key: 'expectedDuration',
      render: (_, record: TemplateTask) =>
        formatDuration(record.expectedDuration),
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (_, record: TemplateTask) => formatDateOnly(record.dueDate || ''),
    },
    {
      title: 'Budgeted Hours',
      align: 'right',
      dataIndex: 'budgetedHours',
      key: 'budgetedHours',
      render: (_, record: TemplateTask) => {
        switch (record.type) {
          case TASK_TYPE_TASK:
            return <Hours amount={record.budgetedHours} />;
          case TASK_TYPE_GROUP:
            return <b>-</b>;
          default:
            return <span />;
        }
      },
    },
    {
      title: 'Assignees',
      dataIndex: 'assignees',
      key: 'assignees',
      render: (_, record: TemplateTask) =>
        (record.assignees || []).map((assignee) => (
          <div key={`${assignee.type}:${assignee.assignee}`}>
            {ASSIGNEE_TYPE_LABELS[assignee.type] ?? assignee.type}
            {assignee.assignee ? `: ${assignee.assignee}` : ''}
          </div>
        )),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (_, record: TemplateTask) => (
        <Flex justify="flex-start" align="center" gap={8}>
          <div>
            {record.type === TASK_TYPE_MILESTONE && (
              <IBDIcon
                name={record.type}
                style={{ marginLeft: '4px', marginRight: '6px' }}
              />
            )}
          </div>
          <div>{record.status}</div>
        </Flex>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record: TemplateTask) => (
        <Flex justify="center" align="center" gap={8}>
          <Tooltip title="Edit Task" placement="top">
            <Button
              icon={<EditOutlined />}
              shape="circle"
              onClick={() => onEditTask(record)}
            />
          </Tooltip>
          <Tooltip title="Delete Task" placement="top">
            <Button
              icon={<DeleteOutlined />}
              shape="circle"
              danger
              onClick={() => onDeleteTask(record)}
            />
          </Tooltip>
        </Flex>
      ),
    },
  ];

  return (
    <DndProvider backend={HTML5Backend}>
      <IBTable<HierarchicalTemplateTask>
        showSorterTooltip
        columns={columns}
        dataSource={taskHierarchy}
        loading={false}
        rowKey="id"
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
          expandIcon: ({ expanded, onExpand, record }) => {
            if (isNotNull(record.children) && record.children.length > 0) {
              return (
                <span
                  onClick={(evt) => onExpand(record, evt)}
                  style={{ cursor: 'pointer', marginRight: 8 }}
                >
                  {expanded ? '▼' : '▶'}
                </span>
              );
            }
            return <span style={{ marginRight: 8 }}>&nbsp;</span>;
          },
        }}
        components={{
          body: {
            row: DraggableTaskRow,
          },
        }}
        onRow={(record, index) =>
          ({
            index,
            record,
            moveTask: onMoveTask,
          }) as React.HTMLAttributes<HTMLElement>
        }
      />
    </DndProvider>
  );
};

export default TemplateTasksTable;
